import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";

import { i18n } from "../../translate/i18n";

function MenuIcon({ color, children }) {
	return (
		<span
			className="tailwick-composer-attach-menu__icon"
			style={{ color }}
			aria-hidden
		>
			{children}
		</span>
	);
}

function FileMenuItem({ id, accept, disabled, onChange, icon, label, onClose }) {
	return (
		<div className="tailwick-composer-attach-menu__file-item">
			<input
				multiple
				type="file"
				id={id}
				accept={accept}
				disabled={disabled}
				className="hidden"
				onChange={(event) => {
					onChange(event);
					onClose();
				}}
			/>
			<label
				htmlFor={id}
				className={`tailwick-composer-attach-menu__item${
					disabled ? " tailwick-composer-attach-menu__item--disabled" : ""
				}`}
				role="menuitem"
			>
				{icon}
				<span>{label}</span>
			</label>
		</div>
	);
}

export default function TailwickComposerAttachMenu({
	open,
	anchorEl,
	onClose,
	disabled,
	darkMode,
	onChangeMedias,
	onCameraClick,
	onOpenContacts,
	onOpenSchedule,
	onOpenPix,
	signMessage,
	onSignMessageChange,
}) {
	const [menuStyle, setMenuStyle] = useState(null);

	useEffect(() => {
		if (!open || !anchorEl) {
			setMenuStyle(null);
			return undefined;
		}

		const updatePosition = () => {
			const rect = anchorEl.getBoundingClientRect();
			const menuWidth = 220;
			const left = Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8));
			setMenuStyle({
				top: rect.top - 8,
				left,
				transform: "translateY(-100%)",
			});
		};

		updatePosition();
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);

		return () => {
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [open, anchorEl]);

	useEffect(() => {
		if (!open) return undefined;

		const handlePointerDown = (event) => {
			const menuNode = document.getElementById("tailwick-composer-attach-menu");
			if (menuNode?.contains(event.target)) return;
			if (anchorEl?.contains(event.target)) return;
			onClose();
		};

		document.addEventListener("mousedown", handlePointerDown);
		return () => document.removeEventListener("mousedown", handlePointerDown);
	}, [open, anchorEl, onClose]);

	if (!open || !menuStyle) return null;

	const menu = (
		<div
			id="tailwick-composer-attach-menu"
			className="tailwick-composer-attach-menu"
			data-theme={darkMode ? "dark" : "light"}
			role="menu"
			style={{
				position: "fixed",
				top: menuStyle.top,
				left: menuStyle.left,
				transform: menuStyle.transform,
				zIndex: 1400,
			}}
		>
			<FileMenuItem
				id="tailwick-upload-document"
				accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
				disabled={disabled}
				onChange={onChangeMedias}
				onClose={onClose}
				label="Documento"
				icon={
					<MenuIcon color="#7F66FF">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
							<path d="M14 2v6h6" />
						</svg>
					</MenuIcon>
				}
			/>

			<FileMenuItem
				id="tailwick-upload-media"
				accept="image/*,video/*"
				disabled={disabled}
				onChange={onChangeMedias}
				onClose={onClose}
				label="Fotos e vídeos"
				icon={
					<MenuIcon color="#017BFC">
						<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<rect width="18" height="18" x="3" y="3" rx="2" />
							<circle cx="9" cy="9" r="2" />
							<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
						</svg>
					</MenuIcon>
				}
			/>

			<button
				type="button"
				role="menuitem"
				className="tailwick-composer-attach-menu__item"
				disabled={disabled}
				onClick={() => {
					onCameraClick();
					onClose();
				}}
			>
				<MenuIcon color="#FF3074">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
						<circle cx="12" cy="13" r="3" />
					</svg>
				</MenuIcon>
				<span>Câmera</span>
			</button>

			<button
				type="button"
				role="menuitem"
				className="tailwick-composer-attach-menu__item"
				disabled={disabled}
				onClick={() => {
					onOpenContacts();
					onClose();
				}}
			>
				<MenuIcon color="#019DE1">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
						<circle cx="12" cy="7" r="4" />
					</svg>
				</MenuIcon>
				<span>Contato</span>
			</button>

			<button
				type="button"
				role="menuitem"
				className="tailwick-composer-attach-menu__item"
				disabled={disabled}
				onClick={() => {
					onOpenSchedule();
					onClose();
				}}
			>
				<MenuIcon color="#ff5d00">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<rect width="18" height="18" x="3" y="4" rx="2" />
						<path d="M16 2v4M8 2v4M3 10h18" />
					</svg>
				</MenuIcon>
				<span>Agendar</span>
			</button>

			<button
				type="button"
				role="menuitem"
				className="tailwick-composer-attach-menu__item"
				disabled={disabled}
				onClick={() => {
					onOpenPix();
					onClose();
				}}
			>
				<MenuIcon color="#017BFC">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
					</svg>
				</MenuIcon>
				<span>PIX</span>
			</button>

			<div className="tailwick-composer-attach-menu__toggle" role="menuitem">
				<span>{i18n.t("messagesInput.signMessage")}</span>
				<input
					type="checkbox"
					className="form-checkbox"
					checked={signMessage}
					disabled={disabled}
					onChange={(event) => onSignMessageChange(event.target.checked)}
				/>
			</div>
		</div>
	);

	return ReactDOM.createPortal(menu, document.body);
}
